import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Tooltip,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FlightTakeoff as DroneIcon,
  Battery80 as BatteryIcon,
  CameraAlt as PayloadIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useOrganization } from '../../context/OrganizationContext';
import { useAuth0 } from '@auth0/auth0-react';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import styles from '../../styles/Analytics.module.css';

/**
 * UtilizationReports Page
 *
 * Displays fleet utilization analytics:
 * - Drone utilization rates
 * - Battery cycle counts
 * - Payload usage
 * - Underutilized assets
 *
 * Access: Owner, Admin, Manager roles only
 */
function UtilizationReports() {
  const { currentOrganization, userRole } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30'); // days
  const [utilization, setUtilization] = useState({
    drones: [],
    batteries: [],
    payloads: [],
    averageDroneUtilization: 0,
    underutilizedAssets: []
  });

  // Check if user has permission to view analytics
  const canView = ['owner', 'admin', 'manager'].includes(userRole?.toLowerCase());

  // Fetch utilization data
  useEffect(() => {
    if (!currentOrganization?.organizationId || !canView) return;

    const fetchUtilizationData = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const orgUuid = currentOrganization.organizationUuid;
        const authHeader = { Authorization: `Bearer ${token}` };

        const [utilizationResp, batteriesResp, payloadsResp] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/analytics/fleet-utilization`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeader },
            body: JSON.stringify({
              organizationId: currentOrganization.organizationId,
              timeRangeDays: parseInt(timeRange)
            })
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/batteries`, {
            headers: authHeader
          }),
          fetch(`${process.env.REACT_APP_API_URL}/api/organization/${orgUuid}/payloads`, {
            headers: authHeader
          })
        ]);

        if (!utilizationResp.ok) {
          throw new Error('Failed to fetch utilization data');
        }

        const [data, batteriesData, payloadsData] = await Promise.all([
          utilizationResp.json(),
          batteriesResp.ok ? batteriesResp.json() : { batteries: [] },
          payloadsResp.ok ? payloadsResp.json() : { payloads: [] }
        ]);

        const batteries = (batteriesData.batteries || []).map((b) => ({
          id: b.id,
          name: b.nickname || `${b.manufacturer} ${b.model}`,
          serialNumber: b.serialNumber,
          cycleCount: b.cycleCount || 0,
          healthPercentage: b.healthPercentage,
          status: b.status,
          health: getBatteryHealth(b.cycleCount || 0)
        }));

        const payloads = (payloadsData.payloads || []).map((p) => ({
          id: p.id,
          name: p.nickname || `${p.manufacturer} ${p.model}`,
          type: p.type,
          status: p.status,
          weight: p.weight
        }));

        setUtilization({
          drones: data.drones || [],
          batteries,
          payloads,
          averageDroneUtilization: data.averageDroneUtilization || 0,
          underutilizedAssets: data.underutilizedAssets || []
        });
      } catch (err) {
        setError('Failed to load utilization reports. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUtilizationData();
  }, [currentOrganization, timeRange, canView, getAccessTokenSilently]);

  const getUtilizationColor = (rate) => {
    if (rate >= 25) return 'success';  // Good: 25%+ (10+ hours/week)
    if (rate >= 10) return 'warning';   // Medium: 10-25% (4-10 hours/week)
    return 'error';                     // Low: <10% (<4 hours/week)
  };

  const getBatteryHealth = (cycles) => {
    if (cycles < 100) return 'Excellent';
    if (cycles < 200) return 'Good';
    if (cycles < 300) return 'Fair';
    return 'Replace Soon';
  };

  const getBatteryHealthColor = (cycles) => {
    if (cycles < 100) return 'success';
    if (cycles < 200) return 'primary';
    if (cycles < 300) return 'warning';
    return 'error';
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  if (!canView) {
    return (
      <OrganizationLayout>
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            You do not have permission to view this page. Only Owners, Admins, and Managers can access analytics.
          </Alert>
        </Box>
      </OrganizationLayout>
    );
  }

  return (
    <OrganizationLayout>
      <Container maxWidth="xl" sx={{ mt: isMobile ? 2 : 4, mb: 4, px: isMobile ? 2 : 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 2 : 0 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AssessmentIcon sx={{ fontSize: isMobile ? 24 : 32, color: '#1976d2' }} />
              <Typography variant={isMobile ? "h5" : "h4"} component="h1">
                Utilization Reports
              </Typography>
            </Box>
            <Typography variant={isMobile ? "body2" : "body1"} color="textSecondary">
              Track fleet asset utilization and identify optimization opportunities.
            </Typography>
          </Box>

          {/* Time Range Filter */}
          <FormControl sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="7">Last 7 Days</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
              {/* Average Drone Utilization */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <TrendingUpIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ cursor: isMobile ? 'default' : 'help', fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Avg Utilization
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {utilization.averageDroneUtilization}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Fleet average
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Drones */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <DroneIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Drones
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {utilization.drones.length}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Total fleet
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Batteries */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <BatteryIcon color="primary" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Batteries
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {utilization.batteries.length}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Total inventory
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Underutilized Assets */}
              <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
                <Card elevation={2} className={styles.metricCard} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 }, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1 }}>
                      <WarningIcon color="warning" sx={{ fontSize: isMobile ? 18 : 24 }} />
                      <Typography variant="subtitle2" color="textSecondary" sx={{ fontSize: isMobile ? '0.65rem' : '0.875rem' }}>
                        Underutilized
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 600, color: '#ff9800', fontSize: isMobile ? '1.5rem' : '3rem' }}>
                      {utilization.underutilizedAssets.length}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      Assets {'<'}10% usage
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Utilization Explanation */}
            <Alert severity="info" sx={{ mb: isMobile ? 1.5 : 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                How Utilization is Calculated:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                Utilization % = (Flight Hours / Available Hours) × 100
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                • Based on 40 hours/week operational capacity (8 hours/day, 5 days/week)<br />
                • 25%+ (10+ hrs/week) = Good utilization<br />
                • 10-25% (4-10 hrs/week) = Medium utilization<br />
                • Below 10% (less than 4 hrs/week) = Low utilization
              </Typography>
            </Alert>

            {/* Drone Utilization Table */}
            <Card elevation={2} sx={{ mb: isMobile ? 1.5 : 3 }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                  Drone Utilization
                </Typography>
                {utilization.drones.length > 0 ? (
                  isMobile ? (
                    <Stack spacing={1.5}>
                      {utilization.drones.map((drone) => (
                        <Card key={drone.id} variant="outlined">
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                                {drone.name || 'Unnamed'}
                              </Typography>
                              <Chip
                                label={drone.status}
                                size="small"
                                color={getUtilizationColor(parseFloat(drone.utilizationRate))}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1 }}>
                              {drone.model}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  Flights
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {drone.flightCount}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  Hours
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {drone.flightHours}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  Utilization
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {drone.utilizationRate}%
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                                  Flights/Week
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                  {drone.flightsPerWeek || '0.0'}
                                </Typography>
                              </Box>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={parseFloat(drone.utilizationRate)}
                              color={getUtilizationColor(parseFloat(drone.utilizationRate))}
                              sx={{ width: '100%', height: 6, borderRadius: 1 }}
                            />
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Drone</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Flights</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              <Tooltip title="Flight hours as % of available hours (40 hrs/week capacity)">
                                <span>Utilization</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Flights/Week</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Visual</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {utilization.drones.map((drone) => (
                            <TableRow key={drone.id}>
                              <TableCell>{drone.name || 'Unnamed'}</TableCell>
                              <TableCell>{drone.model}</TableCell>
                              <TableCell>{drone.flightCount}</TableCell>
                              <TableCell>{drone.flightHours} hrs</TableCell>
                              <TableCell>
                                <Tooltip title={`${drone.flightHours} hours flown out of available capacity`}>
                                  <span>{drone.utilizationRate}%</span>
                                </Tooltip>
                              </TableCell>
                              <TableCell>{drone.flightsPerWeek || '0.0'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={drone.status}
                                  size="small"
                                  color={getUtilizationColor(parseFloat(drone.utilizationRate))}
                                />
                              </TableCell>
                              <TableCell>
                                <LinearProgress
                                  variant="determinate"
                                  value={parseFloat(drone.utilizationRate)}
                                  color={getUtilizationColor(parseFloat(drone.utilizationRate))}
                                  sx={{ width: 100, height: 8, borderRadius: 1 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    No drones found
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Battery Health & Payload Usage */}
            <Grid container spacing={isMobile ? 1.5 : 3}>
              {/* Battery Health */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                      Battery Health
                    </Typography>
                    {utilization.batteries.length > 0 ? (
                      isMobile ? (
                        <Stack spacing={1}>
                          {utilization.batteries.slice(0, 10).map((battery) => (
                            <Box key={battery.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box sx={{ flex: 1, mr: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.25 }}>
                                  {battery.name}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  {battery.cycleCount} cycles
                                </Typography>
                              </Box>
                              <Chip
                                label={battery.health}
                                size="small"
                                color={getBatteryHealthColor(battery.cycleCount)}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Battery</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Cycles</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Health</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {utilization.batteries.slice(0, 10).map((battery) => (
                                <TableRow key={battery.id}>
                                  <TableCell>{battery.name}</TableCell>
                                  <TableCell>{battery.cycleCount}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={battery.health}
                                      size="small"
                                      color={getBatteryHealthColor(battery.cycleCount)}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        No batteries found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Payload Usage */}
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                    <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 1.5 : 2, fontSize: isMobile ? '0.95rem' : '1.25rem' }}>
                      Payload Usage
                    </Typography>
                    {utilization.payloads.length > 0 ? (
                      isMobile ? (
                        <Stack spacing={1}>
                          {utilization.payloads.slice(0, 10).map((payload) => (
                            <Box key={payload.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Box sx={{ flex: 1, mr: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, mb: 0.25 }}>
                                  {payload.name}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                  {payload.type} • {payload.weight ?? '—'}g
                                </Typography>
                              </Box>
                              <Chip
                                label={payload.status || 'Active'}
                                size="small"
                                color={payload.status?.toLowerCase() === 'active' ? 'success' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Payload</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Weight (g)</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {utilization.payloads.slice(0, 10).map((payload) => (
                                <TableRow key={payload.id}>
                                  <TableCell>{payload.name}</TableCell>
                                  <TableCell>{payload.type}</TableCell>
                                  <TableCell>{payload.weight ?? '—'}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={payload.status || 'Active'}
                                      size="small"
                                      color={payload.status?.toLowerCase() === 'active' ? 'success' : 'default'}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )
                    ) : (
                      <Typography variant="body2" color="textSecondary" align="center" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                        No payloads found
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Underutilized Assets Warning */}
            {utilization.underutilizedAssets.length > 0 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {utilization.underutilizedAssets.length} Underutilized Asset(s) Detected
                </Typography>
                <Typography variant="body2">
                  The following assets have less than 20% utilization. Consider redistributing workloads or reviewing asset allocation.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {utilization.underutilizedAssets.map((asset, idx) => (
                    <Chip
                      key={idx}
                      label={`${asset.name} (${asset.utilizationRate}%)`}
                      size="small"
                      sx={{ mr: 1, mt: 1 }}
                    />
                  ))}
                </Box>
              </Alert>
            )}
          </>
        )}
      </Container>
    </OrganizationLayout>
  );
}

export default UtilizationReports;
